export interface SegmentedOption<T extends string> {
  id: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  /** Announced as the group name — "Duration", "Transaction type", … */
  ariaLabel: string;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * The segmented control, in one place.
 *
 * The audit found five hand-rolled identities for the same control —
 * an ORANGE active pill, a green one, a dark pill, a tertiary-on-tertiary
 * (invisible) one, and ViewToggle's. This renders the ViewToggle active
 * identity (primary tint) on the shared track, with aria-pressed states.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  size = 'sm',
  className = '',
}: SegmentedControlProps<T>) {
  const pad = size === 'sm' ? 'px-3 py-1 text-sm' : 'px-4 py-1.5 text-sm';
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`inline-flex bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg p-1 ${className}`}
    >
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          aria-pressed={value === opt.id}
          onClick={() => onChange(opt.id)}
          className={`${pad} rounded-md transition-colors ${
            value === opt.id
              ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300 shadow-sm font-medium'
              : 'text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default SegmentedControl;
